import * as THREE from 'three';

export interface ParticleSpawn {
  x: number;
  y: number;
  z: number;
  vx?: number;
  vy?: number;
  vz?: number;
  life: number;
  size: number;
  color: THREE.ColorRepresentation;
  gravity?: number;
  drag?: number;
  /** Size multiplier at end of life. */
  grow?: number;
}

/**
 * Pooled point-sprite particles with per-particle size/alpha, rendered in a single draw call.
 */
export class ParticleSystem {
  readonly points: THREE.Points;
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly size: Float32Array;
  private readonly alpha: Float32Array;
  private readonly vel: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly baseSize: Float32Array;
  private readonly params: Float32Array; // gravity, drag, grow
  private cursor = 0;
  private readonly tmp = new THREE.Color();

  constructor(
    private readonly max: number,
    additive: boolean,
  ) {
    this.pos = new Float32Array(max * 3);
    this.col = new Float32Array(max * 3);
    this.size = new Float32Array(max);
    this.alpha = new Float32Array(max);
    this.vel = new Float32Array(max * 3);
    this.life = new Float32Array(max);
    this.maxLife = new Float32Array(max);
    this.baseSize = new Float32Array(max);
    this.params = new Float32Array(max * 3);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('color', new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('size', new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('alpha', new THREE.BufferAttribute(this.alpha, 1).setUsage(THREE.DynamicDrawUsage));
    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      uniforms: { scale: { value: 300 } },
      vertexShader: /* glsl */ `
        attribute float size;
        attribute float alpha;
        attribute vec3 color;
        uniform float scale;
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          vColor = color;
          vAlpha = alpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * scale / max(0.1, -mv.z);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        varying vec3 vColor;
        varying float vAlpha;
        void main() {
          vec2 c = gl_PointCoord - 0.5;
          float d = length(c);
          if (d > 0.5) discard;
          float a = smoothstep(0.5, 0.0, d) * vAlpha;
          gl_FragColor = vec4(vColor, a);
        }`,
    });
    this.points = new THREE.Points(geo, mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 5;
  }

  setPixelScale(viewportHeight: number): void {
    (this.points.material as THREE.ShaderMaterial).uniforms.scale!.value = viewportHeight * 0.6;
  }

  spawn(p: ParticleSpawn): void {
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % this.max;
    this.pos[i * 3] = p.x;
    this.pos[i * 3 + 1] = p.y;
    this.pos[i * 3 + 2] = p.z;
    this.vel[i * 3] = p.vx ?? 0;
    this.vel[i * 3 + 1] = p.vy ?? 0;
    this.vel[i * 3 + 2] = p.vz ?? 0;
    this.tmp.set(p.color);
    this.col[i * 3] = this.tmp.r;
    this.col[i * 3 + 1] = this.tmp.g;
    this.col[i * 3 + 2] = this.tmp.b;
    this.life[i] = p.life;
    this.maxLife[i] = p.life;
    this.baseSize[i] = p.size;
    this.params[i * 3] = p.gravity ?? 0;
    this.params[i * 3 + 1] = p.drag ?? 0;
    this.params[i * 3 + 2] = p.grow ?? 1;
  }

  update(dt: number): void {
    const { pos, vel, life, maxLife, params, alpha, size, baseSize } = this;
    for (let i = 0; i < this.max; i++) {
      if (life[i] <= 0) {
        alpha[i] = 0;
        continue;
      }
      life[i] -= dt;
      const remaining = life[i];
      const t = 1 - Math.max(0, remaining) / maxLife[i];
      const drag = 1 - Math.min(1, params[i * 3 + 1] * dt);
      const ix = i * 3;
      vel[ix] *= drag;
      vel[ix + 1] = vel[ix + 1] * drag - params[ix] * dt;
      vel[ix + 2] *= drag;
      pos[ix] += vel[ix] * dt;
      pos[ix + 1] += vel[ix + 1] * dt;
      pos[ix + 2] += vel[ix + 2] * dt;
      alpha[i] = remaining <= 0 ? 0 : t < 0.1 ? t * 10 : 1 - (t - 0.1) / 0.9;
      size[i] = baseSize[i] * (1 + (params[ix + 2] - 1) * t);
    }
    const geo = this.points.geometry;
    geo.attributes.position!.needsUpdate = true;
    geo.attributes.color!.needsUpdate = true;
    geo.attributes.size!.needsUpdate = true;
    geo.attributes.alpha!.needsUpdate = true;
  }
}
